import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { Package, Check, Zap, CreditCard, CheckCircle, XCircle, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Modules = () => {
  const { token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [modules, setModules] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingModule, setProcessingModule] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null); // 'success', 'cancelled', 'processing'
  const [pollingSessionId, setPollingSessionId] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [modulesRes, subsRes] = await Promise.all([
        axios.get(`${API}/modules`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/subscriptions`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setModules(modulesRes.data);
      setSubscriptions(subsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Poll for payment status
  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setPaymentStatus('timeout');
      setPollingSessionId(null);
      return;
    }

    try {
      const response = await axios.get(`${API}/stripe/checkout/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.payment_status === 'paid') {
        setPaymentStatus('success');
        setPollingSessionId(null);
        fetchData(); // Refresh subscriptions
        // Clear URL params
        setSearchParams({});
        return;
      } else if (response.data.status === 'expired') {
        setPaymentStatus('expired');
        setPollingSessionId(null);
        setSearchParams({});
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error polling payment status:', error);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      }
    }
  }, [token, fetchData, setSearchParams]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle return from Stripe
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const success = searchParams.get('success');
    const cancelled = searchParams.get('cancelled');

    if (sessionId && success === 'true') {
      setPaymentStatus('processing');
      setPollingSessionId(sessionId);
      pollPaymentStatus(sessionId);
    } else if (cancelled === 'true') {
      setPaymentStatus('cancelled');
      setTimeout(() => {
        setPaymentStatus(null);
        setSearchParams({});
      }, 3000);
    }
  }, [searchParams, pollPaymentStatus, setSearchParams]);

  const handleSubscribe = async (moduleId) => {
    setProcessingModule(moduleId);
    try {
      const response = await axios.post(
        `${API}/stripe/checkout`,
        {
          module_id: moduleId,
          origin_url: window.location.origin
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.checkout_url) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      alert('Erreur lors de la création du paiement. Veuillez réessayer.');
      setProcessingModule(null);
    }
  };

  const isSubscribed = (moduleId) => subscriptions.some(s => s.module_id === moduleId);

  const getModuleIcon = (name) => {
    if (name.includes('Vote')) return '🗳️';
    if (name.includes('Visio') || name.includes('Video')) return '📹';
    if (name.includes('Signature')) return '✍️';
    if (name.includes('Stockage')) return '💾';
    return '📦';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00D3A8]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6" data-testid="modules-page">
      {/* Payment Status Banner */}
      {paymentStatus && (
        <div className={`rounded-lg p-3 md:p-4 flex items-center space-x-3 ${
          paymentStatus === 'success' ? 'bg-green-50 border border-green-200' :
          paymentStatus === 'cancelled' ? 'bg-yellow-50 border border-yellow-200' :
          paymentStatus === 'processing' ? 'bg-blue-50 border border-blue-200' :
          paymentStatus === 'expired' ? 'bg-red-50 border border-red-200' :
          'bg-gray-50 border border-gray-200'
        }`}>
          {paymentStatus === 'success' && (
            <>
              <CheckCircle className="text-green-500 flex-shrink-0" size={24} />
              <div>
                <p className="font-medium text-green-800 text-sm md:text-base">Paiement réussi !</p>
                <p className="text-xs md:text-sm text-green-600">Votre abonnement a été activé avec succès.</p>
              </div>
            </>
          )}
          {paymentStatus === 'cancelled' && (
            <>
              <XCircle className="text-yellow-500" size={24} />
              <div>
                <p className="font-medium text-yellow-800">Paiement annulé</p>
                <p className="text-sm text-yellow-600">Vous pouvez réessayer quand vous le souhaitez.</p>
              </div>
            </>
          )}
          {paymentStatus === 'processing' && (
            <>
              <Loader2 className="text-blue-500 animate-spin" size={24} />
              <div>
                <p className="font-medium text-blue-800">Vérification du paiement...</p>
                <p className="text-sm text-blue-600">Veuillez patienter pendant que nous confirmons votre paiement.</p>
              </div>
            </>
          )}
          {paymentStatus === 'expired' && (
            <>
              <XCircle className="text-red-500" size={24} />
              <div>
                <p className="font-medium text-red-800">Session expirée</p>
                <p className="text-sm text-red-600">Votre session de paiement a expiré. Veuillez réessayer.</p>
              </div>
            </>
          )}
          {paymentStatus === 'timeout' && (
            <>
              <XCircle className="text-gray-500" size={24} />
              <div>
                <p className="font-medium text-gray-800">Vérification en attente</p>
                <p className="text-sm text-gray-600">Nous n'avons pas pu confirmer le paiement. Vérifiez vos factures.</p>
              </div>
            </>
          )}
        </div>
      )}

      <div>
        <h1 className="text-2xl font-bold text-gray-800">Modules</h1>
        <p className="text-gray-500 mt-1 text-sm md:text-base">Améliorez votre expérience avec nos modules premium</p>
      </div>

      {/* Subscriptions Summary */}
      {subscriptions.length > 0 && (
        <div className="bg-gradient-to-r from-[#00D3A8] to-[#00C7A1] rounded-lg p-3 md:p-4 text-white">
          <div className="flex items-center space-x-2 mb-2">
            <Check size={20} />
            <span className="font-medium text-sm md:text-base">Vos abonnements actifs</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {subscriptions.map((sub) => {
              const module = modules.find(m => m.id === sub.module_id);
              return (
                <span key={sub.id} className="bg-white/20 rounded-full px-3 py-1 text-xs md:text-sm">
                  {module?.name || sub.module_id}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Modules Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6" data-testid="modules-grid">
        {modules.map((module) => {
          const subscribed = isSubscribed(module.id);
          const isProcessing = processingModule === module.id;
          
          return (
            <div 
              key={module.id} 
              data-testid={`module-card-${module.id}`}
              className={`bg-white rounded-xl shadow-lg overflow-hidden border-2 transition-all ${
                subscribed ? 'border-green-500' : 'border-transparent hover:border-[#00D3A8]'
              }`}
            >
              <div className="p-4 md:p-6">
                <div className="flex items-start justify-between mb-3 md:mb-4">
                  <div className="text-3xl md:text-4xl">{getModuleIcon(module.name)}</div>
                  {subscribed && (
                    <span className="bg-green-100 text-green-700 text-xs px-2 md:px-3 py-1 rounded-full flex items-center">
                      <Check size={14} className="mr-1" />Actif
                    </span>
                  )}
                </div>
                <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-2">{module.name}</h3>
                <p className="text-gray-500 text-xs md:text-sm mb-4">{module.description}</p>
                
                {module.features && (
                  <ul className="space-y-2 mb-4 md:mb-6">
                    {module.features.map((feature, i) => (
                      <li key={i} className="flex items-center text-sm text-gray-600">
                        <Check size={16} className="mr-2 text-green-500 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                )}

                <div className="flex items-end justify-between pt-4 border-t border-gray-100">
                  <div>
                    <span className="text-3xl font-bold text-gray-800">{module.price?.toFixed(2)}</span>
                    <span className="text-gray-500 ml-1">€/mois</span>
                  </div>
                  
                  {subscribed ? (
                    <button 
                      disabled 
                      className="bg-gray-100 text-gray-500 py-2 px-6 rounded-lg cursor-not-allowed"
                      data-testid={`module-subscribed-${module.id}`}
                    >
                      Déjà abonné
                    </button>
                  ) : (
                    <button
                      onClick={() => handleSubscribe(module.id)}
                      disabled={isProcessing || pollingSessionId}
                      className="bg-[#00D3A8] hover:bg-[#00C7A1] text-white py-2 px-6 rounded-lg transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid={`module-subscribe-${module.id}`}
                    >
                      {isProcessing ? (
                        <>
                          <Loader2 size={18} className="animate-spin" />
                          <span>Chargement...</span>
                        </>
                      ) : (
                        <>
                          <CreditCard size={18} />
                          <span>S'abonner</span>
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {modules.length === 0 && (
        <div className="text-center py-12 text-gray-500" data-testid="no-modules">
          <Package size={48} className="mx-auto mb-4 text-gray-300" />
          <p>Aucun module disponible</p>
        </div>
      )}

      {/* Secure Payment Badge */}
      <div className="flex items-center justify-center space-x-2 text-gray-400 text-sm">
        <CreditCard size={16} />
        <span>Paiement sécurisé par Stripe</span>
      </div>
    </div>
  );
};

export default Modules;
